/**
 * Iter 73 — shared helpers for the Force-CTR Test Seeder input. The
 * NotificationsPage previously inlined this logic in 3 IIFEs
 * (legendCounts memo, preview chips, in-range aggregate chip); a
 * lint/review note surfaced the duplication so it lives here now.
 *
 * The parsing rules match the backend contract at
 * POST /api/admin/analytics/seed-threshold-history:
 *   - 2 to 10 numeric entries, each 0..100 inclusive.
 * Anything outside those bounds returns null so callers can render
 * "invalid" states uniformly.
 */

export const RANGE_BOUNDS = {
  spread: [0, 100],
  low: [0, 30],
  mid: [30, 70],
  high: [70, 100],
};

export const RANGE_KEYS = ["spread", "low", "mid", "high"];

/**
 * Parses a comma-separated CTR input string.
 * @param {string} raw - user-entered value.
 * @returns {number[] | null} - array of validated numbers, or null.
 */
export function parseSeedValues(raw) {
  const parts = (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const nums = parts.map((p) => Number(p));
  const allValid = nums.length >= 2 && nums.every((n) => Number.isFinite(n) && n >= 0 && n <= 100);
  return allValid ? nums : null;
}

/**
 * Counts how many of the provided values fall inside a named range.
 * Unknown range keys fall back to "spread" so callers don't crash on
 * bad localStorage payloads.
 */
export function countInRange(values, rangeKey) {
  const [lo, hi] = RANGE_BOUNDS[rangeKey] || RANGE_BOUNDS.spread;
  return values.filter((v) => v >= lo && v <= hi).length;
}

/**
 * Picks the range key that maximises the in-range count for a given
 * value list. "spread" trivially covers 0-100 so it's excluded from
 * the candidate list and only returned when none of low/mid/high has
 * any hits (safe default). Ties among low/mid/high break by the order
 * ["low", "mid", "high"] so behaviour stays deterministic. Returns
 * null when `values` is empty.
 */
export function bestFitRangeKey(values) {
  if (!values || values.length === 0) return null;
  const CANDIDATES = ["low", "mid", "high"];
  let bestKey = CANDIDATES[0];
  let bestCount = -1;
  for (const key of CANDIDATES) {
    const n = countInRange(values, key);
    if (n > bestCount) {
      bestCount = n;
      bestKey = key;
    }
  }
  return bestCount > 0 ? bestKey : "spread";
}
